import { useEffect, useState, type CSSProperties } from 'react';
import QR from 'qrcode';
import { cn } from './cn';

/**
 * Ant Design `QRCode` replacement — renders a real, scannable QR (e.g. UPI
 * `upi://pay?...` payment strings) via the `qrcode` library as an inline SVG.
 * Fully offline; keeps a white quiet-zone so it scans in dark mode too.
 */
export interface QRCodeProps {
  value: string;
  size?: number;
  bordered?: boolean;
  color?: string;
  bgColor?: string;
  errorLevel?: 'L' | 'M' | 'Q' | 'H';
  className?: string;
  style?: CSSProperties;
}

export function QRCode({
  value, size = 160, bordered = true, color = '#141414', bgColor = '#ffffff', errorLevel = 'M', className, style,
}: QRCodeProps) {
  const [svg, setSvg] = useState('');
  useEffect(() => {
    let alive = true;
    if (!value) { setSvg(''); return; }
    QR.toString(value, { type: 'svg', errorCorrectionLevel: errorLevel, margin: 1, color: { dark: color, light: bgColor } })
      .then((s) => { if (alive) setSvg(s); })
      .catch(() => { if (alive) setSvg(''); });
    return () => { alive = false; };
  }, [value, color, bgColor, errorLevel]);

  return (
    <div
      className={cn('inline-grid place-items-center overflow-hidden bg-white [&>svg]:size-full', bordered && 'rounded-lg border border-line p-2', className)}
      style={{ width: size, height: size, ...style }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
